import { z } from 'zod';

import { ImageSizeWithOriginalInUriSchemaBuilder } from '../../utils/image-size';
import { makeEndpoint } from '../common';

export enum CampaignHeroImageSizes {
  W640 = '640',
  W750 = '750',
  W1080 = '1080',
  W1200 = '1200',
  W1920 = '1920',
  W2048 = '2048',
}

export enum CampaignExperienceType {
  CULINARY_BEVERAGE = 'Culinary & Beverage',
  ART_CULTURE_HISTORY = 'Art, Culture, & History',
  ADVENTURE_OUTDOOR = 'Adventure & Outdoor',
  WELLNESS_MOVEMENT = 'Wellness & Movement',
  LEARNING_SKILLBUILDING = 'Learning & Skillbuilding',
  OTHER = 'Other',
}

export enum CampaignStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const CampaignHeroImageSizeWithOriginalInUriSchema = ImageSizeWithOriginalInUriSchemaBuilder(
  Object.values(CampaignHeroImageSizes),
);

export const CampaignHeroPhotoSchema = z.object({
  id: z.string().uuid(),
  uri: CampaignHeroImageSizeWithOriginalInUriSchema,
});

export const CreateCampaignRequestSchema = z
  .object({
    slug: z
      .string()
      .min(1, 'Slug is required')
      .max(30, 'Slug must be at most 30 characters')
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens'),
    name: z.string().min(1, 'Name is required'),
    description: z.string().nullable().optional(),
    heroHeadline: z.string().nullable().optional(),
    heroSubtext: z.string().nullable().optional(),
    heroPhotoId: z.string().uuid('Hero photo ID must be a valid UUID'),
    experienceHeadline: z.string().nullable().optional(),
    experienceSubtext: z.string().nullable().optional(),
    experienceTypes: z.array(z.nativeEnum(CampaignExperienceType)).min(1, 'At least one experience type is required'),
    reachHeadline: z.string().nullable().optional(),
    reachSubtext: z.string().nullable().optional(),
    status: z.nativeEnum(CampaignStatus).optional().default(CampaignStatus.ACTIVE),
  })
  .strict();

export type CreateCampaignRequestType = z.infer<typeof CreateCampaignRequestSchema>;

export const UpdateCampaignRequestSchema = z
  .object({
    slug: z
      .string()
      .max(30, 'Slug must be at most 30 characters')
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens')
      .optional(),
    name: z.string().min(1, 'Name is required').optional(),
    description: z.string().nullable().optional(),
    heroHeadline: z.string().nullable().optional(),
    heroSubtext: z.string().nullable().optional(),
    heroPhotoId: z.string().uuid('Hero photo ID must be a valid UUID').optional(),
    experienceHeadline: z.string().nullable().optional(),
    experienceSubtext: z.string().nullable().optional(),
    experienceTypes: z
      .array(z.nativeEnum(CampaignExperienceType))
      .min(1, 'At least one experience type is required')
      .optional(),
    reachHeadline: z.string().nullable().optional(),
    reachSubtext: z.string().nullable().optional(),
    status: z.nativeEnum(CampaignStatus).optional(),
  })
  .strict()
  .partial();

export type UpdateCampaignRequestType = z.infer<typeof UpdateCampaignRequestSchema>;

export const CampaignResponseSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  brandId: z.string().uuid(),
  description: z.string().nullable(),
  heroHeadline: z.string().nullable(),
  heroSubtext: z.string().nullable(),
  heroPhoto: CampaignHeroPhotoSchema.nullable(),
  experienceHeadline: z.string().nullable(),
  experienceSubtext: z.string().nullable(),
  experienceTypes: z.array(z.nativeEnum(CampaignExperienceType)).nullable(),
  reachHeadline: z.string().nullable(),
  reachSubtext: z.string().nullable(),
  status: z.nativeEnum(CampaignStatus),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CampaignResponseType = z.infer<typeof CampaignResponseSchema>;

export const CreateCampaignEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/campaign`,
  responseSchema: CampaignResponseSchema,
  requestSchema: CreateCampaignRequestSchema,
});

export const GetCampaignEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/campaign`,
  responseSchema: CampaignResponseSchema.nullable(),
  requestSchema: z.void(),
});

export const ListCampaignsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/campaigns`,
  responseSchema: z.array(CampaignResponseSchema),
  requestSchema: z.void(),
});

export const UpdateCampaignEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId, campaignId }: { brandId: string; campaignId: string }) => `v1/brands/${brandId}/campaigns/${campaignId}`,
  responseSchema: CampaignResponseSchema,
  requestSchema: UpdateCampaignRequestSchema,
});

export const DeleteCampaignEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/campaign`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const PublicCampaignResponseSchema = CampaignResponseSchema.pick({
  id: true,
  slug: true,
  name: true,
  brandId: true,
  description: true,
  heroHeadline: true,
  heroSubtext: true,
  heroPhoto: true,
  experienceHeadline: true,
  experienceSubtext: true,
  experienceTypes: true,
  reachHeadline: true,
  reachSubtext: true,
  status: true,
});

export type PublicCampaignResponseType = z.infer<typeof PublicCampaignResponseSchema>;

export const GetPublicCampaignEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ slug }: { slug: string }) => `v1/campaigns/${slug}`,
  responseSchema: PublicCampaignResponseSchema.nullable(),
  requestSchema: z.void(),
});
